import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Family from "@/pages/Family";
import Curriculum from "@/pages/Curriculum";
import TopicPage from "@/pages/TopicPage";
import ParentDashboard from "@/pages/ParentDashboard";
import ParentExamHistory from "@/pages/ParentExamHistory";
import AdminDiagrams from "@/pages/AdminDiagrams";
import AdminConsole from "@/pages/AdminConsole";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Winback from "@/pages/Winback";
import WelcomeWizard from "@/pages/WelcomeWizard";
import Dashboard from "@/pages/Dashboard";
import SubjectDetail from "@/pages/SubjectDetail";
import MockExam from "@/pages/MockExam";
import MockExamRun from "@/pages/MockExamRun";
import Leaderboard from "@/pages/Leaderboard";
import Refer from "@/pages/Refer";
import WaitlistLanding from "@/pages/WaitlistLanding";
import { PrivacyPolicy, TermsOfService } from "@/pages/Legal";
import Footer from "@/components/Footer";
import Layout from "@/components/Layout";

function Protected({ children, requirePremium = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center font-display">
        <div className="text-2xl font-extrabold">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requirePremium && !["active", "trialing"].includes(user.subscription_status)) {
    return <Navigate to="/pricing" replace />;
  }
  return children;
}

function HomeRoute() {
  // Public home = waitlist landing. The landing page itself redirects
  // logged-in users to /family (or /welcome if the wizard isn't done).
  return <WaitlistLanding />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/winback/:token" element={<Winback />} />

      <Route path="/" element={<HomeRoute />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/welcome" element={<Protected><WelcomeWizard /></Protected>} />
      <Route path="/family" element={<Protected><Family /></Protected>} />
      <Route path="/curriculum" element={<Protected><Curriculum /></Protected>} />
      <Route path="/curriculum/topic/:topicId" element={<Protected><TopicPage /></Protected>} />
      <Route path="/parent" element={<Protected><ParentDashboard /></Protected>} />
      <Route path="/parent/exam-history" element={<Protected><ParentExamHistory /></Protected>} />
      <Route path="/exam" element={<Protected><MockExam /></Protected>} />
      <Route path="/exam/:examId" element={<Protected requirePremium><MockExamRun /></Protected>} />
      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/refer" element={<Protected><Refer /></Protected>} />
      <Route path="/admin/diagrams" element={<AdminDiagrams />} />
      <Route path="/admin/console" element={<AdminConsole />} />
      <Route path="/admin" element={<AdminConsole />} />

      {/* Premium: personal upload */}
      <Route path="/subjects" element={<Protected requirePremium><Layout><Dashboard /></Layout></Protected>} />
      <Route path="/subjects/:id" element={<Protected requirePremium><Layout><SubjectDetail /></Layout></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function GlobalFooter() {
  // Hide footer on the auth-callback hash flow so it doesn't flash during redirect.
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return null;
  return <Footer />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <GlobalFooter />
          <Toaster
            position="top-right"
            toastOptions={{
              className: "nb-border rounded-xl bg-white dark:bg-zinc-900 text-black dark:text-white font-semibold",
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
